/**
 * OperationGrouper
 * Groups related operations for Blender-style undo/redo
 *
 * Note: Operations have dynamic `details` payloads whose shapes vary by type,
 * requiring `any` casts when accessing sub-properties.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { Operation } from '@/shared/types/projectData';
import { GroupingRule, transformRules } from './groupingRules/transformRules';
import { editRules } from './groupingRules/editRules';
import { organizeRules } from './groupingRules/organizeRules';
import { navigateRules } from './groupingRules/navigateRules';
import { compoundRules } from './groupingRules/compoundRules';

export interface OperationGroup {
  id: string;
  name: string;
  operations: Operation[];
  startTime: number;
  endTime: number;
  category: 'transform' | 'edit' | 'organize' | 'navigate' | 'compound';
  canMerge: boolean;
  metadata?: {
    itemType?: string;
    field?: string;
    elementId?: string;
    affectedCount?: number;
  };
}

export class OperationGrouper {
  private activeGroup: OperationGroup | null = null;
  private groupTimeout: NodeJS.Timeout | null = null;
  private readonly MERGE_WINDOW = 2000; // 2 seconds to group related ops

  // Merge all grouping rules from extracted modules
  private readonly GROUPING_RULES: Record<string, GroupingRule> = {
    ...transformRules,
    ...editRules,
    ...organizeRules,
    ...navigateRules,
    ...compoundRules
  };

  public processOperation(operation: Operation): OperationGroup {
    // For UIOperations, use the operationType from details
    const operationType = operation.type === 'ui-state-change'
      ? (operation as any).details?.operationType
      : operation.type;

    const rule = this.GROUPING_RULES[operationType];

    // Check if we should merge with active group
    if (this.activeGroup && this.shouldMerge(operation, this.activeGroup, rule)) {
      // Replace the active group instead of mutating the object returned for the
      // previous operation. History entries can therefore safely retain a snapshot.
      this.activeGroup = {
        ...this.activeGroup,
        operations: [...this.activeGroup.operations, operation],
        endTime: Date.now(),
        metadata: this.activeGroup.metadata ? { ...this.activeGroup.metadata } : undefined,
      };
      this.updateGroupMetadata(this.activeGroup, operation);
      this.extendGroupTimeout();
      return this.activeGroup;
    }

    // Close active group and start new one
    this.closeActiveGroup();

    // Create new group
    this.activeGroup = {
      id: `group-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      name: rule?.name(operation) || operation.description || this.capitalizeType(operation.type),
      operations: [operation],
      startTime: Date.now(),
      endTime: Date.now(),
      category: rule?.opensGroup || 'edit',
      canMerge: rule?.canMerge !== false,
      metadata: this.extractMetadata(operation)
    };

    this.startGroupTimeout(rule?.mergeWindow);
    return this.activeGroup;
  }

  private shouldMerge(operation: Operation, group: OperationGroup, rule?: GroupingRule): boolean {
    // Can't merge if group doesn't allow merging
    if (!group.canMerge) return false;

    // Time-based merging
    const mergeWindow = rule?.mergeWindow || this.MERGE_WINDOW;
    if (Date.now() - group.endTime > mergeWindow) return false;

    // Category-based merging
    if (rule?.opensGroup && rule.opensGroup !== group.category) return false;

    // Custom merge conditions
    if (rule?.mergeCondition) {
      const lastOp = group.operations[group.operations.length - 1];
      return rule.mergeCondition(lastOp, operation);
    }

    // Default: merge operations of same type within same category
    const lastOpType = group.operations[group.operations.length - 1].type;
    return lastOpType === operation.type;
  }

  private updateGroupMetadata(group: OperationGroup, operation: Operation) {
    // Update group name based on accumulated operations
    const operationType = operation.type === 'ui-state-change'
      ? (operation as any).details?.operationType
      : operation.type;

    const rule = this.GROUPING_RULES[operationType];
    if (rule) {
      // For multi-operation groups, use more descriptive names
      if (group.operations.length > 1) {
        switch (group.category) {
          case 'organize':
            if ((operation as any).details?.operationType === 'select-node') {
              const lastSelection = (operation as any).details?.after?.selected || [];
              group.name = lastSelection.length > 1 ? `Select ${lastSelection.length} items` : 'Select item';
            }
            break;
          case 'edit':
            if ((operation as any).details?.operationType === 'text-edit') {
              group.name = `Edit ${(operation as any).details?.field || 'text'}`;
            }
            break;
          case 'transform':
            if (operation.type.startsWith('drag-')) {
              const dragType = (operation as any).details?.dragType || 'item';
              group.name = `Move ${dragType}`;
            }
            break;
        }
      }
    }

    // Update metadata
    if (!group.metadata) group.metadata = {};

    // Track affected elements
    const nodeId = (operation as any).nodeId || (operation as any).details?.nodeId;
    if (nodeId) {
      group.metadata.elementId = nodeId;
    }

    // Track operation count
    group.metadata.affectedCount = group.operations.length;
  }

  private extractMetadata(operation: Operation): OperationGroup['metadata'] {
    const metadata: OperationGroup['metadata'] = {};

    // Extract item type
    if ((operation as any).details?.dragType) {
      metadata.itemType = (operation as any).details.dragType;
    } else if ((operation as any).data?.type) {
      metadata.itemType = (operation as any).data.type;
    }

    // Extract field name for edits
    if ((operation as any).details?.field) {
      metadata.field = (operation as any).details.field;
    }

    // Extract element ID
    const nodeId = (operation as any).nodeId || (operation as any).details?.nodeId;
    if (nodeId) {
      metadata.elementId = nodeId;
    }

    metadata.affectedCount = 1;

    return metadata;
  }

  private capitalizeType(type: string): string {
    return type.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  private extendGroupTimeout() {
    if (this.groupTimeout) {
      clearTimeout(this.groupTimeout);
    }
    this.startGroupTimeout();
  }

  private startGroupTimeout(customWindow?: number) {
    const timeout = customWindow || this.MERGE_WINDOW;
    this.groupTimeout = setTimeout(() => {
      this.closeActiveGroup();
    }, timeout);
  }

  public closeActiveGroup(): OperationGroup | null {
    const group = this.activeGroup;
    this.activeGroup = null;

    if (this.groupTimeout) {
      clearTimeout(this.groupTimeout);
      this.groupTimeout = null;
    }

    return group;
  }

  public getActiveGroup(): OperationGroup | null {
    return this.activeGroup;
  }

  public forceCloseGroup(): OperationGroup | null {
    return this.closeActiveGroup();
  }

  // For debugging and testing
  public getGroupingStats() {
    return {
      hasActiveGroup: !!this.activeGroup,
      activeGroupSize: this.activeGroup?.operations.length || 0,
      activeGroupName: this.activeGroup?.name,
      activeGroupCategory: this.activeGroup?.category
    };
  }
}
